import { colors } from "../constants/designTokens";

const ignoredTimelineKeys = new Set(["mes_ano", "idClienteEmpresa", "nomeEmpresa"]);

function firstDefined(item, keys, fallback = "") {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && String(item[key]).trim() !== "") {
      return item[key];
    }
  }
  return fallback;
}

function toNumber(value) {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

function monthOrder(value) {
  const [month, year] = String(value || "").split("/").map((part) => Number(part));
  return Number.isFinite(month) && Number.isFinite(year) ? year * 100 + month : 0;
}

export function normalizeEmpreendimentoIntersecao(item) {
  const idClienteObra = firstDefined(item, ["idClienteObra", "id_cliente_obra", "id"], "");
  const nomeObra = firstDefined(item, ["nomeObra", "nome_obra", "obraArquivo", "obra_arquivo", "codigoObraErp", "codigo_obra_erp"], "");
  const nomeEmpresa = firstDefined(item, ["nomeEmpresa", "nome_empresa", "empresaArquivo", "empresa_arquivo"], "");

  return {
    idClienteEmpresa: firstDefined(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    codigoEmpresaErp: firstDefined(item, ["codigoEmpresaErp", "codigo_empresa_erp"], ""),
    idClienteObra,
    nomeEmpresa,
    nomeObra: nomeObra || (idClienteObra ? `Empreendimento ${idClienteObra}` : ""),
  };
}

export function normalizeIntersecaoList(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const uniqueItems = new Map();
  value
    .map(normalizeEmpreendimentoIntersecao)
    .filter((item) => item.nomeEmpresa || item.nomeObra)
    .forEach((item) => {
      const companyKey = item.codigoEmpresaErp || item.idClienteEmpresa;
      const key = companyKey
        ? `empresa:${companyKey}`
        : `obra:${item.idClienteObra || item.nomeObra}`;

      if (!uniqueItems.has(key)) {
        uniqueItems.set(key, item);
      }
    });

  return Array.from(uniqueItems.values());
}

export function normalizeIntersecaoPorMes(payload) {
  const source =
    payload?.empreendimentosIntersecaoPorMes ||
    payload?.empreendimentos_intersecao_por_mes ||
    payload?.intersecaoEmpreendimentosPorMes ||
    payload?.intersecao_empreendimentos_por_mes ||
    {};

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }

  return Object.entries(source).reduce((acc, [mes, empreendimentos]) => {
    acc[mes] = normalizeIntersecaoList(empreendimentos);
    return acc;
  }, {});
}

export function getIntersecaoForMonth(data, activeMonth) {
  if (data?.empreendimentosIntersecaoPorMes) {
    return data.empreendimentosIntersecaoPorMes[activeMonth] || [];
  }
  return data?.empreendimentosIntersecao || [];
}

export function consolidateTimelineByMonth(timeline = [], percentKeys = []) {
  const percentSet = new Set(percentKeys);
  const byMonth = new Map();

  timeline.forEach((item) => {
    if (!item?.mes_ano) {
      return;
    }

    const current = byMonth.get(item.mes_ano) || { mes_ano: item.mes_ano, quantidade: 0 };
    current.quantidade += 1;

    Object.entries(item).forEach(([key, value]) => {
      if (ignoredTimelineKeys.has(key)) {
        return;
      }
      current[key] = toNumber(current[key]) + toNumber(value);
    });

    byMonth.set(item.mes_ano, current);
  });

  return Array.from(byMonth.values())
    .sort((left, right) => monthOrder(left.mes_ano) - monthOrder(right.mes_ano))
    .map((item) => {
      const quantidade = item.quantidade || 1;
      const normalized = { ...item };
      delete normalized.quantidade;

      percentSet.forEach((key) => {
        normalized[key] = toNumber(normalized[key]) / quantidade;
      });

      return normalized;
    });
}

export const intersecaoNoticeStyles = {
  borderRadius: 2,
  bgcolor: colors.surface,
  p: 1.5,
  mb: 2,
  border: `1px solid ${colors.border}`,
};

export function resolveActiveMonth(months = [], selectedMonth = "") {
  const availableMonths = Array.from(new Set(months.filter(Boolean)));
  if (!availableMonths.length) return "";
  if (selectedMonth && availableMonths.includes(selectedMonth)) return selectedMonth;
  if (!selectedMonth) return availableMonths[availableMonths.length - 1];

  const selectedOrder = monthOrder(selectedMonth);
  if (!selectedOrder) return availableMonths[availableMonths.length - 1];

  const ordered = availableMonths
    .map((month) => ({ month, order: monthOrder(month) }))
    .filter((item) => item.order > 0)
    .sort((left, right) => left.order - right.order);

  const previous = ordered.filter((item) => item.order < selectedOrder).pop();
  if (previous) return previous.month;

  return ordered.find((item) => item.order > selectedOrder)?.month || availableMonths[availableMonths.length - 1];
}
